import React, { Component } from "react";
import Button from "react-bootstrap/Button";

import labExercises from "../assets/images/Lab_Exercises.pdf";

class ExercisesPage extends Component {
  constructor(props) {
    super(props);

    this.state = {};
  }
  componentWillMount() {
    document.title = "Exercises";
  }

  componentDidMount() {
    this.focusedItem.focus();
  }

  render() {
    return (
      <div className="page-container container pt-4">
        <a name="skipnav" id="skipnav">
          <h1>Lab Exercises</h1>
        </a>
        <p className="py-3">
          The possibility to modify the parameters of the Neurosphere Simulator
          enables instructors to design a multitude of teaching lab exercises.
          The three examples below are also available as a single PDF by
          clicking on the “Download Lab Exercises” button. For background
          information and interpretation of the results, we recommend to consult
          the papers by <a
            className="b-link"
            href="https://doi.org/10.1016/j.jtbi.2018.02.025"
            target="_blank"
            rel="noopener noreferrer"
          >
            Sipahi and Zupanc (2018)
          </a> and <a
            className="b-link"
            href="https://doi.org/10.1016/j.jtbi.2019.01.028"
            target="_blank"
            rel="noopener noreferrer"
          >
            Zupanc et al. (2019)
          </a>.
        </p>
        <div className="pb-5 text-center">
          <a href={labExercises} download="Lab_Exercises.pdf">
            <Button
              variant="primary"
              ref={(focus) => {
                this.focusedItem = focus;
              }}
            >
              Download Lab Exercises
            </Button>
          </a>
        </div>
        <h2 className="pb-3">
          Effect of founder cell type on neurosphere growth
        </h2>
        <p>
          Biological neurospheres exhibit an enormous heterogeneity in size,
          even when derived through passaging from the same parental neurosphere
          and cultured under identical conditions. It has been postulated that
          size differences within a given neurosphere population are related to
          the identity of the founder cell, with bona fide stem cells giving
          rise to larger neurospheres than progenitor cells with more restricted
          proliferative potential (
          <a
            className="b-link"
            href="https://doi.org/10.1634/stemcells.2007-0867"
            target="_blank"
            rel="noopener noreferrer"
          >
            Louis et al., 2008
          </a>
          ). However, this hypothesis has been questioned by other investigators (
          <a
            className="b-link"
            href="https://doi.org/10.1016/j.stem.2011.04.007"
            target="_blank"
            rel="noopener noreferrer"
          >
            Pastrana et al., 2011
          </a>
          ).
        </p>

        <p>
          <i>Hypothesis</i>: In this lab exercise the student will test the
          hypothesis that stem cells as founder cells give rise to larger
          neurospheres than do progenitor cells as founder cells.
        </p>

        <p>
          <i>Approach</i>: Run two sets of simulations: one in which an active
          stem cell is used as a founder cell, and another in which a progenitor
          cell is used as a founder cell. In all simulations, set the
          proliferative potential of the progenitor cells to 7 and select the
          large animation lattice size. For all the other parameters, use the
          default settings of the simulator.
        </p>

        <p>
          <i>Tasks</i>: Run 10 simulations for each of the two sets and
          calculate the mean size of neurospheres by counting the number of
          ‘live’ cells (= stem cell, progenitor cells, and differentiated
          cells).
        </p>

        <p>
          Provide snapshots at iteration step = 100 of simulated neurospheres
          grown under the two conditions. Based on the visual inspection of
          these simulated neurospheres, what might limit the growth of the
          neurospheres, even though the stem cell has unlimited proliferative
          potential, and the proliferative potential of the progenitor cells may
          not be exhausted?
        </p>

        <h2 className="py-3">
          Effect of proliferative potential of progenitor cells on neurosphere
          growth
        </h2>

        <p>
          Progenitor cells arise from stem cells and undergo a few mitotic
          divisions before they terminally differentiate. This mitotic activity
          of the progenitor cells plays an important role in tissue growth.
        </p>

        <p>
          <i>Hypothesis</i>: In this lab exercise, the student will test the
          hypothesis that the proliferative potential of progenitor cells
          correlates positively with the size of neurospheres.
        </p>

        <p>
          <i>Approach</i>: Select the large animation lattice size and an active
          stem cell as a founder cell. Modify the proliferative potential from
          1-10. For all other parameters, use the default setting of the
          Neurosphere Simulator.
        </p>

        <p>
          <i>Tasks</i>: Run 10 simulations for each proliferative potential of
          the progenitor cells. Plot the mean number of ‘live’ cells as a
          function of the proliferative potential. Do the results support the
          hypothesis?
        </p>

        <h2 className="py-3">
          Tumorigenesis by transformation of progenitor cells into brain tumor
          stem cells
        </h2>

        <p>
          It has been proposed that brain tumors are caused by transformation of
          adult neural stem or progenitor cells into BTSCs (
          <a
            className="b-link"
            href="https://doi.org/10.1056/NEJMra043666"
            target="_blank"
            rel="noopener noreferrer"
          >
            Sanai et al., 2005
          </a>
          ). Based on this
          hypothesis, we will examine how the cellular microenvironment can
          modulate the development of tumorous tissue.
        </p>

        <p>
          <i>Hypothesis</i>: In this lab exercise, we will test the specific
          hypothesis that encapsulation of BTSCs by neighboring non-BTSC cells
          can suppress tumor growth.
        </p>

        <p>
          <i>Approach</i>: Select the large animation lattice size and an active
          stem cell as a founder cell. Use the following parameter settings:
          Proliferative potential of progenitors: 6; Clearance time: 10; Minimum
          probability of mitosis: 0; Maximum probability of mitosis: 1; Minimum
          probability of cell death: 0.05; Maximum probability of cell death:
          0.5; Transformation to BTSC: yes; Time steps until transformation: 10.
          For the other parameters, use the default settings of the Neurosphere
          Simulator.
        </p>

        <p>
          <i>Task</i>: Run 50 simulations with these parameter settings. Plot
          the frequency distribution of the size of these 50 simulated
          neurospheres, as assessed by determining the number of ‘live’ cells
          for each neurosphere. What is notable about the distribution? Does
          each neurosphere develop into tumorous tissue? Inspect the animated
          cellular structure of the different types of neurospheres. What role
          does encapsulation of BTSCs play for the promotion of tumorous growth?
        </p>
      </div>
    );
  }
}

export default ExercisesPage;
